import {
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
} from '../../common/domain/mappingAdmin';
import { DUPLICATE_VALIDATION_COLUMN_IDS } from './constants';

export const findDuplicateId = (
  rowToCheck:
    | FieldMappingDataRow
    | FixingProxyMappingDataRow
    | LegalEntityMappingDataRow,
  rowData: (
    | FieldMappingDataRow
    | FixingProxyMappingDataRow
    | LegalEntityMappingDataRow
  )[],
  activeTab: string,
) => {
  const fieldsDuplicateChecked = DUPLICATE_VALIDATION_COLUMN_IDS[activeTab];
  for (const row of rowData) {
    const allFieldsMatch = fieldsDuplicateChecked.every(
      (column: string) => row[column] === rowToCheck[column],
    );
    const validTo = row.validTo || '';
    const datesOverlap =
      new Date(row.validFrom) <= new Date(rowToCheck.validTo) &&
      (new Date(validTo) >= new Date(rowToCheck.validFrom) || validTo === '');
    if (allFieldsMatch && datesOverlap) {
      return row.id;
    }
  }
};

/** ISO 8601 date or datetime pattern (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss, optional Z/z or offset). */
const ISO_TIMESTAMP_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}([.,]\d{1,9})?(Z|z|[+-]\d{2}:?\d{2})?)?$/;

/** Returns true if value is empty or a valid date/timestamp (parseable, not Invalid Date). */
export const isValidTimestamp = (value: unknown): boolean => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return true;
  }
  const d = new Date(value as string | number | Date);
  return !Number.isNaN(d.getTime());
};

/** Returns true if value is empty or matches ISO 8601 date/datetime format (e.g. YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.000Z). */
export const isISOFormattedTimestamp = (value: unknown): boolean => {
  if (value == null) return true;
  const raw = typeof value === 'string' ? value : String(value);
  const s = raw
    .replace(/^\uFEFF/, '') // BOM
    .trim()
    .replace(/^["']+|["']+$/g, ''); // surrounding double or single quotes
  if (s === '') return true;
  if (ISO_TIMESTAMP_REGEX.test(s)) return true;
  // Fallback: accept parseable date that looks like ISO (YYYY-MM-DD with T and Z/offset)
  const looksLikeIso =
    /^\d{4}-\d{2}-\d{2}T.+[Zz]$/.test(s) ||
    /^\d{4}-\d{2}-\d{2}T.+\d{2}:?\d{2}$/.test(s);
  if (looksLikeIso) {
    const ms = Date.parse(s);
    if (Number.isFinite(ms)) return true;
  }
  return false;
};

/** Format a value as ISO timestamp for CSV export; empty for null/undefined. */
export const formatTimestampForCsvExport = (value: unknown): string => {
  if (value == null || value === '') return '';
  try {
    return new Date(value as string | number | Date).toISOString();
  } catch {
    return String(value);
  }
};

/** Returns a processCellCallback for exportDataAsCsv so timestamp columns are in ISO format. */
export const getCsvProcessCellCallback = (
  timestampColumnIds: readonly string[],
) => (params: { column: { getColId: () => string }; value: unknown }) => {
  const colId = params.column.getColId();
  if (timestampColumnIds.includes(colId)) {
    return formatTimestampForCsvExport(params.value);
  }
  return params.value != null ? String(params.value) : '';
};
